import { Badge, Banner, Button, InlineCopyText } from '@cloudflare/kumo';
import { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { DangerConfirmModal } from '../../components/DangerConfirmModal';
import { EmptyState } from '../../components/ui/EmptyState';
import { Glyph, ICONS } from '../../components/ui/icons';
import { useTenant } from '../../contexts/TenantContext';
import { useAuthenticatedFetch } from '../../hooks/useAuthenticatedFetch';
import { useAppToast } from '../../hooks/useAppToast';
import { ToolsetLogo, type ToolsetDetail, type ToolsetTool } from './toolCatalogShared';
import { PageSkeleton } from '../../components/ui/Skeleton';

/** Exemplo de como o agente chama a ferramenta (o servidor monta isto de verdade a cada chamada) */
function example(t: ToolsetTool, authType: ToolsetDetail['authType']) {
  const path = t.params.filter((p) => t.url.includes(`{${p.name}}`));
  const rest = t.params.filter((p) => !t.url.includes(`{${p.name}}`));
  const sample = (p: { name: string; type?: string }) => (p.type === 'number' ? '123' : p.type === 'array' ? '["..."]' : p.type === 'object' ? '{ ... }' : `<${p.name}>`);
  let url = t.url;
  for (const p of path) url = url.replace(`{${p.name}}`, sample(p));
  const lines = [`curl -X ${t.method} '${url}${t.method === 'GET' || t.method === 'DELETE' ? rest.map((p, i) => `${url.includes('?') || i ? '&' : '?'}${p.name}=${sample(p)}`).join('') : ''}'`];
  if (authType !== 'none') lines.push("  -H 'Authorization: Bearer ••••••'  # a credencial vem do cofre");
  if (t.method !== 'GET' && t.method !== 'DELETE') lines.push("  -H 'Content-Type: application/json'", `  -d '{ ${rest.map((p) => `"${p.name}": ${p.type === 'string' || !p.type ? `"${sample(p)}"` : sample(p)}`).join(', ')} }'`);
  return lines.join(' \\\n');
}

/** IA › Ferramentas › <integração>: no molde do detalhe de um modelo da Cloudflare */
export function AiToolDetailPage() {
  const { slug = '' } = useParams();
  const navigate = useNavigate();
  const { can } = useTenant();
  const fetchApi = useAuthenticatedFetch();
  const toast = useAppToast();
  const [t, setT] = useState<ToolsetDetail | null>(null);
  const [missing, setMissing] = useState(false);
  const [open, setOpen] = useState<string | null>(null);
  const [removing, setRemoving] = useState(false);
  const canInstall = can('integrations:manage');

  const load = useCallback(async () => {
    const r = await fetchApi(`/api/ai/tool-catalog/${slug}`);
    const j = await r.json().catch(() => ({}));
    if (!r.ok) { setMissing(true); return; }
    setT(j.toolset); setOpen((cur) => cur ?? j.toolset.tools[0]?.name ?? null);
  }, [fetchApi, slug]);
  useEffect(() => { load(); }, [load]);

  const uninstall = async () => {
    const r = await fetchApi(`/api/ai/tool-catalog/${slug}`, { method: 'DELETE' });
    const j = await r.json().catch(() => ({}));
    setRemoving(false);
    if (!r.ok) { toast.error(j.message || 'Não foi possível desinstalar'); return; }
    toast.success('Integração desinstalada');
    load();
  };

  if (missing) return <div className="px-4 pt-7 md:px-10"><EmptyState icon={ICONS.MagnifyingGlass} title="Integração não encontrada" action={<Link to="/ia/ferramentas"><Button variant="secondary">Voltar ao catálogo</Button></Link>} /></div>;
  if (!t) return <PageSkeleton label="Carregando ferramenta" />;

  return (
    <div className="px-4 pb-10 pt-7 md:px-10">
      <nav className="mb-5 flex items-center gap-1 text-sm text-kumo-subtle" aria-label="Você está em">
        <Link to="/ia" className="hover:underline">IA</Link><span>›</span>
        <Link to="/ia/ferramentas" className="hover:underline">Ferramentas</Link><span>›</span>
        <span className="text-kumo-default">{t.name}</span>
      </nav>

      <header className="mb-8 flex flex-wrap items-start justify-between gap-4">
        <div className="flex min-w-0 items-start gap-4">
          <ToolsetLogo name={t.name} category={t.category} size={56} />
          <div className="min-w-0">
            <h1 className="flex flex-wrap items-center gap-2 text-2xl font-semibold text-kumo-default">{t.name}{t.installed && <Badge variant="green">Instalada</Badge>}</h1>
            <div className="mt-1 flex flex-wrap items-center gap-2 text-sm text-kumo-subtle">{t.author}<Badge variant="neutral">{t.category}</Badge>{t.writes && <Badge variant="orange">Grava dados</Badge>}</div>
            <div className="mt-3 max-w-md"><InlineCopyText value={t.slug} labels={{ copyAction: "Copiar ID", copied: "Copiado" }}>{t.slug}</InlineCopyText></div>
          </div>
        </div>
        {canInstall && (
          <div className="flex gap-2">
            {t.installed
              ? <Button variant="secondary-destructive" onClick={() => setRemoving(true)} data-testid="desinstalar">Desinstalar</Button>
              : <Button variant="primary" onClick={() => navigate(`/ia/ferramentas/${t.slug}/instalar`)} data-testid="instalar">Instalar</Button>}
          </div>
        )}
      </header>

      <div className="grid gap-10 lg:grid-cols-[minmax(0,1fr)_320px]">
        <div className="min-w-0 space-y-10">
          <section>
            <p className="max-w-3xl text-kumo-default">{t.description}</p>
            <p className="mt-2 max-w-3xl text-kumo-subtle">{t.about}</p>
            {t.installed && <div className="mt-4"><Banner variant="default" description={<>Instalada. Ligue as ferramentas no agente em <Link to="/ia/agentes">IA › Agentes › Ferramentas</Link>.</>} /></div>}
          </section>

          <section>
            <h2 className="mb-3 text-lg font-semibold text-kumo-default">Ferramentas ({t.tools.length})</h2>
            <div className="divide-y divide-kumo-line rounded-lg border border-kumo-line">
              {t.tools.map((x) => (
                <div key={x.name} data-testid={`ferramenta-${x.name}`}>
                  <button type="button" className="flex w-full items-start justify-between gap-3 px-4 py-3 text-left hover:bg-kumo-tint" aria-expanded={open === x.name} onClick={() => setOpen(open === x.name ? null : x.name)}>
                    <span className="min-w-0">
                      <span className="flex flex-wrap items-center gap-2 font-medium text-kumo-default">{x.label}<Badge variant={x.writes ? 'orange' : 'neutral'}>{x.method}</Badge></span>
                      <span className="mt-1 block text-sm text-kumo-subtle">{x.description}</span>
                    </span>
                    <Glyph icon={open === x.name ? ICONS.CaretUp : ICONS.CaretDown} size={16} />
                  </button>
                  {open === x.name && (
                    <div className="space-y-4 border-t border-kumo-line bg-kumo-tint/40 px-4 py-4">
                      <div>
                        <div className="mb-1 text-xs font-medium uppercase tracking-wide text-kumo-subtle">Nome para o agente</div>
                        <code className="text-sm">{x.agentName}</code>
                      </div>
                      <div>
                        <div className="mb-1 text-xs font-medium uppercase tracking-wide text-kumo-subtle">Como o agente usa</div>
                        <pre className="overflow-x-auto rounded-md border border-kumo-line bg-kumo-base p-3 text-xs leading-relaxed"><code>{example(x, t.authType)}</code></pre>
                      </div>
                      <div>
                        <div className="mb-1 text-xs font-medium uppercase tracking-wide text-kumo-subtle">Parâmetros</div>
                        {x.params.length === 0 ? <p className="text-sm text-kumo-subtle">Nenhum.</p> : (
                          <table className="w-full text-sm">
                            <tbody>
                              {x.params.map((p) => (
                                <tr key={p.name} className="border-t border-kumo-line first:border-t-0 align-top">
                                  <td className="py-2 pr-3"><code>{p.name}</code>{p.required && <span className="ml-1 text-kumo-subtle">(obrigatório)</span>}</td>
                                  <td className="py-2 pr-3 text-kumo-subtle">{p.type ?? 'string'}</td>
                                  <td className="py-2 text-kumo-default">{p.description}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </section>
        </div>

        <aside className="space-y-6">
          <section className="rounded-lg border border-kumo-line p-4">
            <h2 className="mb-2 font-semibold text-kumo-default">Credencial</h2>
            {t.needsCredential
              ? <><div className="text-sm text-kumo-default">{t.secret?.label}</div><p className="mt-1 text-sm text-kumo-subtle">{t.secret?.help}</p><p className="mt-2 text-xs text-kumo-subtle">Fica cifrada no cofre da organização e nunca chega ao modelo.</p></>
              : <p className="text-sm text-kumo-subtle">Esta API é aberta. Não precisa de credencial.</p>}
          </section>
          <section className="rounded-lg border border-kumo-line p-4">
            <h2 className="mb-2 font-semibold text-kumo-default">Informações</h2>
            <dl className="space-y-1 text-sm">
              <div className="flex justify-between gap-2"><dt className="text-kumo-subtle">Categoria</dt><dd>{t.category}</dd></div>
              <div className="flex justify-between gap-2"><dt className="text-kumo-subtle">Autor</dt><dd>{t.author}</dd></div>
              <div className="flex justify-between gap-2"><dt className="text-kumo-subtle">Ferramentas</dt><dd>{t.toolCount}</dd></div>
              <div className="flex justify-between gap-2"><dt className="text-kumo-subtle">Acesso</dt><dd>{t.writes ? 'Leitura e escrita' : 'Somente leitura'}</dd></div>
            </dl>
          </section>
          <section className="rounded-lg border border-kumo-line p-4">
            <h2 className="mb-2 font-semibold text-kumo-default">Recursos</h2>
            <a href={t.docsUrl} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-sm text-kumo-link hover:underline">Documentação da API <Glyph icon={ICONS.OpenNewTab} size={12} /></a>
          </section>
        </aside>
      </div>

      <DangerConfirmModal open={removing} title={`Desinstalar ${t.name}`} confirmText="Desinstalar" objectName={t.name} onConfirm={uninstall} onCancel={() => setRemoving(false)}>
        As {t.installedTools.length} ferramentas saem dos agentes que as usam e a credencial é apagada do cofre.
      </DangerConfirmModal>
    </div>
  );
}
